import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { useNotification } from '../../../contexts/NotificationContext';
import { usePuppyMode } from '../../../contexts/PuppyModeContext';

/**
 * Enforces the Puppy/Dogfood-mode access boundary on a chat opened via a
 * direct URL, mirroring the legacy admin's `useRestrictChatAccess`:
 *
 * - In Puppy mode a normal (non-puppy) chat is off-limits.
 * - In Normal mode a puppy chat is off-limits.
 * - The `all` filter mode is an explicit escape hatch and imposes no boundary.
 *
 * On a mismatch we raise an explanatory notification and redirect to `/chats`
 * so a stale/shared link cannot bypass the mode filter that scopes the list.
 *
 * @param isPuppyChat Whether the chat is a puppy chat. `undefined` while the
 * chat is still loading — no enforcement runs until the flag resolves.
 */
export const useRestrictChatAccess = (
  isPuppyChat: boolean | undefined
): void => {
  const { filterMode } = usePuppyMode();
  const { addNotification } = useNotification();
  const navigate = useNavigate();

  useEffect(() => {
    // Wait until the chat (and therefore its puppy flag) has loaded.
    if (isPuppyChat === undefined) {
      return;
    }

    const blockedInPuppyMode = filterMode === 'puppy' && !isPuppyChat;
    const blockedInNormalMode = filterMode === 'normal' && isPuppyChat;

    if (!blockedInPuppyMode && !blockedInNormalMode) {
      return;
    }

    addNotification({
      title: 'Restricted chat access',
      message: blockedInPuppyMode
        ? 'This is a normal chat and is hidden while Puppy mode is on. Turn off Puppy mode to open it.'
        : 'This is a Puppy chat and is only visible in Puppy mode. Turn on Puppy mode to open it.',
      type: 'info',
    });
    navigate('/chats', { replace: true });
  }, [isPuppyChat, filterMode, addNotification, navigate]);
};
